import React from "react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { CASE_STUDIES, getCaseStudy } from "../lib/caseStudies";

export const CaseStudiesIndexPage = () => {
    return (
        <section className="section" data-testid="case-studies-index">
            <div className="container">
                <div className="section-head">
                    <span className="eyebrow">Customer stories</span>
                    <h1 className="h1" style={{ marginTop: 18 }}>Real operators. Real outcomes.</h1>
                    <p>Mativex and Vexen run in production for paying operators today. Customer names are withheld at their request — the numbers and outcomes below are real.</p>
                </div>
                <FadeUpGroup>
                    <div className="case-grid">
                        {CASE_STUDIES.map((c) => (
                            <a key={c.slug} href={`/stories/${c.slug}`} className="case-card fade-up" data-testid={`case-card-${c.slug}`}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    <span className="eyebrow" style={{ padding: "4px 10px" }}>{c.industry}</span>
                                    {c.anonymous && (
                                        <span className="chip" style={{ fontSize: "0.7rem", padding: "3px 9px" }}>Anonymised at customer request</span>
                                    )}
                                </div>
                                <div className="muted" style={{ fontSize: "0.85rem", marginTop: 10 }}>{c.nameMask} · {c.scale}</div>
                                <h2 className="h3" style={{ marginTop: 10, fontSize: "1.35rem" }}>{c.headline}</h2>
                                <p className="muted" style={{ marginTop: 10 }}>{c.oneLiner}</p>
                                <div style={{ display: "flex", gap: 14, marginTop: 18, flexWrap: "wrap" }}>
                                    {c.results.slice(0, 2).map((r) => (
                                        <div key={r.l}>
                                            <div style={{ fontFamily: "'Cabinet Grotesk'", fontWeight: 800, color: "var(--accent)", fontSize: "1.5rem", letterSpacing: "-0.01em" }}>{r.v}</div>
                                            <div className="muted" style={{ fontSize: "0.78rem" }}>{r.l}</div>
                                        </div>
                                    ))}
                                </div>
                                <span className="link-arrow" style={{ marginTop: 22, display: "inline-flex" }}>Read story <ArrowRight size={16} /></span>
                            </a>
                        ))}
                    </div>
                </FadeUpGroup>
            </div>
        </section>
    );
};

export const CaseStudyPage = ({ slug }) => {
    const c = getCaseStudy(slug);
    if (!c) {
        return (
            <section className="section">
                <div className="container">
                    <p className="muted">Story not found. <a href="/stories" className="link-arrow">Back to stories →</a></p>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="section" data-testid={`case-study-${c.slug}`}>
                <div className="container" style={{ maxWidth: 860 }}>
                    <a href="/stories" className="link-arrow" data-testid="case-back-link" style={{ marginBottom: 18 }}>
                        <ArrowLeft size={14} /> All stories
                    </a>
                    <FadeUpGroup>
                        <div className="fade-up">
                            <span className="eyebrow">{c.industry}{c.anonymous ? " · Anonymised at customer request" : ""}</span>
                            <h1 className="h1" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)", marginTop: 18 }}>{c.headline}</h1>
                            {(c.nameMask || c.scale) && (
                                <p className="muted" style={{ marginTop: 12, fontSize: "0.95rem" }}>
                                    {c.nameMask}{c.nameMask && c.scale ? " · " : ""}{c.scale}
                                </p>
                            )}
                            <p className="lead" style={{ marginTop: 18 }}>{c.oneLiner}</p>
                        </div>

                        <div className="stat-grid fade-up" style={{ marginTop: 48 }}>
                            {c.results.map((r) => (
                                <div key={r.l} className="stat-card" data-testid={`case-result-${r.l.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <div className="num">{r.v}</div>
                                    <div className="label">{r.l}</div>
                                </div>
                            ))}
                        </div>

                        <div className="card fade-up" style={{ marginTop: 48, padding: "36px 32px" }}>
                            <h2 className="h3" style={{ color: "var(--accent)", fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "0.12em" }}>The challenge</h2>
                            <p className="muted" style={{ marginTop: 12, fontSize: "1rem", lineHeight: 1.7 }}>{c.challenge}</p>
                        </div>

                        <div className="card fade-up" style={{ marginTop: 24, padding: "36px 32px" }}>
                            <h2 className="h3" style={{ color: "var(--accent)", fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "0.12em" }}>The approach</h2>
                            <ul className="feature-list" style={{ marginTop: 16 }}>
                                {c.approach.map((a, i) => (
                                    <li key={i}><span className="check">{i + 1}</span>{a}</li>
                                ))}
                            </ul>
                        </div>

                        <blockquote className="case-quote fade-up" data-testid="case-quote">
                            <Quote size={36} />
                            <p>"{c.quote}"</p>
                            <footer>— {c.attribution}</footer>
                        </blockquote>

                        <div className="fade-up" style={{ marginTop: 32 }}>
                            <span className="eyebrow">Products used</span>
                            <div className="industry-chips" style={{ marginTop: 14 }}>
                                {c.products.map((p) => <span key={p} className="chip" style={{ color: "var(--accent)" }}>{p}</span>)}
                            </div>
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            <section className="section" style={{ paddingTop: 0 }}>
                <div className="container">
                    <div className="cta-banner">
                        <div>
                            <h2 className="h2">Build your story next.</h2>
                            <p className="lead mt-4">Most teams see meaningful wins in the first quarter. Let's plan yours.</p>
                        </div>
                        <a href="/contact" className="btn btn-primary btn-lg" data-testid={`case-cta-${c.slug}`}>
                            Book a Demo <ArrowRight size={18} />
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CaseStudyPage;
